import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, Activity, MapPin, Globe, Clock, RefreshCw } from 'lucide-react';
import { adminAPI } from '../../services/apiService';
import { toast } from 'sonner';

const VisitorAnalytics = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const viewType = searchParams.get('view') || 'live'; // live, today, monthly
  
  const [visitors, setVisitors] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    uniquePages: 0,
    avgTimeOnSite: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  useEffect(() => {
    const isAdminAuth = localStorage.getItem('isAdminAuthenticated');
    if (!isAdminAuth) {
      navigate('/admin/login');
      return;
    }

    loadVisitorData();
    
    // Auto-refresh every 10 seconds
    const interval = setInterval(() => {
      loadVisitorData();
    }, 10000);

    return () => clearInterval(interval);
  }, [navigate, viewType]);

  const loadVisitorData = async () => {
    try {
      const data = await adminAPI.getVisitorDetails(viewType);
      setVisitors(data.visitors || []);
      setStats(data.stats || { total: 0, uniquePages: 0, avgTimeOnSite: 0 });
      setLastRefresh(new Date());
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading visitor data:', error);
      toast.error('Failed to load visitor analytics');
      setIsLoading(false);
    }
  };

  const getTitle = () => {
    switch(viewType) {
      case 'live': return 'Live Visitors';
      case 'today': return "Today's Visitors";
      case 'monthly': return 'Monthly Visitors';
      default: return 'Visitor Analytics';
    }
  };

  const getSubtitle = () => {
    switch(viewType) {
      case 'live': return 'Active in last 5 minutes';
      case 'today': return 'Visitors today';
      case 'monthly': return 'Visitors this month';
      default: return '';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading visitor data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8 bg-white/80 backdrop-blur-sm p-6 rounded-2xl border-4 border-red-300 shadow-xl">
          <div className="flex items-center justify-between mb-4">
            <Button 
              onClick={() => navigate('/admin/dashboard')}
              variant="outline"
              className="border-2 border-red-400"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Clock className="h-4 w-4" />
                Last updated: {lastRefresh.toLocaleTimeString()}
              </div>
              <Button 
                onClick={loadVisitorData}
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
          
          <h1 className="text-4xl font-bold mb-2" style={{
            background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 50%, #ec4899 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>{getTitle()}</h1>
          <p className="text-gray-700 text-lg font-medium">{getSubtitle()}</p>
          {viewType === 'live' && (
            <div className="mt-2 flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm text-green-600 font-semibold">Auto-refreshing every 10 seconds</span>
            </div>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="border-3 border-blue-300 shadow-lg bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-blue-600" />
                Total Visitors
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-blue-600">{stats.total || visitors.length}</p>
            </CardContent>
          </Card>

          <Card className="border-3 border-purple-300 shadow-lg bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-purple-600" />
                Unique Pages
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-purple-600">{stats.uniquePages || 0}</p>
            </CardContent>
          </Card>

          <Card className="border-3 border-green-300 shadow-lg bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-green-600" />
                Unique Sessions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-green-600">{new Set(visitors.map(v => v.sessionId)).size}</p>
            </CardContent>
          </Card>
        </div>

        {/* Visitor Activity Table */}
        <Card className="border-4 border-red-400 shadow-2xl bg-white/90 backdrop-blur-sm">
          <CardHeader>
            <CardTitle>Visitor Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Session ID</TableHead>
                    <TableHead>Page Visited</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>Device</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visitors && visitors.length > 0 ? (
                    visitors.map((visitor, index) => (
                      <TableRow key={index} className="hover:bg-gray-50">
                        <TableCell className="font-mono text-xs">
                          {visitor.sessionId ? visitor.sessionId.substring(0, 12) + '...' : 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="font-medium">
                            {visitor.page || '/'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {visitor.location ? (
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-1 text-sm font-medium">
                                <MapPin className="h-3 w-3 text-red-600" />
                                {visitor.location.city || 'Unknown'}
                              </div>
                              <div className="text-xs text-gray-600">
                                {visitor.location.region ? `${visitor.location.region}, ` : ''}
                                {visitor.location.country || 'Unknown'}
                              </div>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">Location unavailable</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          {new Date(visitor.timestamp).toLocaleString('en-IN', {
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                            day: '2-digit',
                            month: 'short'
                          })}
                        </TableCell>
                        <TableCell className="text-xs max-w-xs truncate">
                          {visitor.userAgent ? (
                            <div className="flex flex-col gap-1">
                              <span className="font-medium">
                                {visitor.userAgent.includes('Mobile') ? '📱 Mobile' : 
                                 visitor.userAgent.includes('Tablet') ? '📱 Tablet' : '💻 Desktop'}
                              </span>
                              <span className="text-gray-500 truncate" title={visitor.userAgent}>
                                {visitor.userAgent.substring(0, 30) + '...'}
                              </span>
                            </div>
                          ) : 'N/A'}
                        </TableCell>
                        <TableCell>
                          {viewType === 'live' ? (
                            <Badge className="bg-green-100 text-green-800">
                              <Activity className="h-3 w-3 mr-1" />
                              Active
                            </Badge>
                          ) : (
                            <Badge variant="outline">Visited</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                        No visitor data available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
            
            {/* Summary Stats */}
            {visitors && visitors.length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Unique Cities</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {new Set(visitors.filter(v => v.location?.city).map(v => v.location.city)).size}
                    </p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Unique Countries</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {new Set(visitors.filter(v => v.location?.country).map(v => v.location.country)).size}
                    </p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Mobile Visitors</p>
                    <p className="text-2xl font-bold text-green-600">
                      {visitors.filter(v => v.userAgent?.includes('Mobile')).length}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VisitorAnalytics;
